import { useEffect, useLayoutEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  FormControl,
  InputGroup,
  Row,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import apiCall from "../../api/api-call";
import ConditionalWrapper from "../../util/ConditionalWrapper";
import Util from "../../util/util";

function EditJobPost() {
  const [disableSave, setDisableSave] = useState(true);
  const [selectedRadio, setSelectedRadio] = useState(null);
  const [details, setDetails] = useState(null);
  const [formState, setFormState] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const { jobid } = useParams();
  const navigate = useNavigate();

  const setInput = (key, value, required = false) => {
    if (disableSave) {
      setDisableSave(false);
    }
    setFormState({ ...formState, [key]: { value, required } });
  };

  const handleDateChange = (date, event) => {
    setStartDate(date);
    setInput("endDate", new Date(date).toISOString(), true);
  };

  const handleSubmitBtn = (event) => {
    const field = { ...formState };
    let errorCounter = 0;
    //console.log("field: ", field);

    if (!jobid) {
      toast.error("Unable to proceed at the moment. Please try again later.");
      return;
    }

    if (!Object.keys(field).length) {
      toast.error("No changes being made.");
      return;
    }

    Object.entries(field).forEach(([key, value]) => {
      //console.log(`${key} ${value.required}`);
      if (value.required && value.value.trim() === "") {
        toast.error(`${value.label} is required.`);
        errorCounter++;
      }
    });

    if (errorCounter > 0) {
      console.log("has error");
      return;
    }

    //console.log("no more error");
    errorCounter = 0;

    // construct request body
    const reqBody = {};
    Object.entries(field).forEach(([key, value]) => {
      reqBody[key] = value.value.trim();
    });

    reqBody["jobid"] = jobid;

    //console.log("reqBody: ", reqBody);

    // disable button before calling backend
    event.target.setAttribute("disabled", "");

    apiCall
      .updateJob(reqBody)
      .then((response) => {
        //console.log("response: ", response);
        //console.log("update job post");
        toast.success("Successfully updated job post!");
        navigate("/post-job");
      })
      .catch((error) => {
        console.log("error: ", error);
        toast.error("Error encountered. Please try again later.");
        event.target.removeAttribute("disabled");
      });
  };

  useLayoutEffect(() => {
    if (!details) {
      return;
    }
    setStartDate(new Date(details.endDate));
    setSelectedRadio(details.category);
  }, [details]);

  useEffect(() => {
    let queryParam = new URLSearchParams();
    queryParam.append("jobid", jobid);

    apiCall
      .getJobDetails(queryParam.toString())
      .then((response) => {
        //console.log("response: ", response);
        if (response.data.response.Items.length) {
          setDetails(response.data.response.Items[0]);
        } else {
          navigate("/404-page");
        }
      })
      .catch((error) => console.log("error: ", error));
  }, [jobid, navigate]);

  return (
    <>
      <div className="row">
        <div className="col-12">
          <h3 className="mb-3">Edit Job Post</h3>
        </div>
      </div>
      <Form>
        <div className="row g-3">
          <div className="col-12 col-sm-6">
            <Form.Group className="mb-3" controlId="formGroupJobTitle">
              <Form.Label>
                Job Title <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Job Title"
                defaultValue={details ? details.title : ""}
                onChange={(event) =>
                  setInput("title", event.target.value, true)
                }
              />
            </Form.Group>
          </div>
          <div className="col-12 col-sm-6">
            <Form.Group className="mb-3" controlId="formGroupLocation">
              <Form.Label>
                Location <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Location"
                defaultValue={details ? details.location : ""}
                onChange={(event) =>
                  setInput("location", event.target.value, true)
                }
              />
            </Form.Group>
          </div>
          <div className="col-12 col-sm-6">
            <Form.Group className="mb-3" controlId="formGroupVacancy">
              <Form.Label>
                Vacancy <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                min={1}
                placeholder="Vacancy"
                defaultValue={details ? details.vacancy : ""}
                onChange={(event) =>
                  setInput("vacancy", event.target.value, true)
                }
              />
            </Form.Group>
          </div>

          <div className="col-12 col-sm-6">
            <Form.Group className="mb-3" controlId="formGroupJobNature">
              <Form.Label>
                Job Nature <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                aria-label="Job Nature"
                defaultValue={details ? details.jobNature : ""}
                onChange={(event) =>
                  setInput("jobNature", event.target.value, true)
                }
              >
                <option>Full Time</option>
                <option>Part Time</option>
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-12 col-sm-6">
            <Form.Group className="mb-3" controlId="formGroupSalary">
              <Form.Label>Salary</Form.Label>
              <InputGroup className="mb-3">
                <InputGroup.Text>$</InputGroup.Text>
                <FormControl
                  aria-label="Amount (to the nearest dollar)"
                  defaultValue={details ? details.salary : ""}
                  onChange={(event) => setInput("salary", event.target.value)}
                />
                <InputGroup.Text>.00</InputGroup.Text>
              </InputGroup>
            </Form.Group>
          </div>
          <div className="col-12 col-sm-6">
            <Form.Group className="mb-3" controlId="formGroupEndDate">
              <Form.Label>
                End Date <span className="text-danger">*</span>
              </Form.Label>
              <DatePicker
                selected={startDate}
                onChange={handleDateChange}
                minDate={new Date()}
                placeholderText="Select a date"
                className="form-control"
              />
            </Form.Group>
          </div>
          <div className="col-12">
            <Form.Label>
              Category <span className="text-danger">*</span>
            </Form.Label>
            <Row>
              {Util.transformCategoryToGroup(2).map((categories, index) => (
                <ConditionalWrapper
                  condition={true}
                  key={index}
                  wrapper={(children) => <Col key={index}>{children}</Col>}
                >
                  <>
                    {categories.map((category, i) => (
                      <Form.Check
                        key={index + i}
                        label={category}
                        value={category}
                        name={`group1`}
                        type="radio"
                        id={`inline-radio-${index + "-" + i}`}
                        checked={selectedRadio === category ? true : false}
                        onChange={(event) => {
                          setInput("category", event.target.value, true);
                          setSelectedRadio(event.target.value);
                        }}
                      />
                    ))}
                  </>
                </ConditionalWrapper>
              ))}
            </Row>
          </div>
          <div className="col-12">
            <Form.Group className="mb-3" controlId="formGroupJobDesc">
              <Form.Label>
                Job Description <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                placeholder="Job Description"
                defaultValue={details ? details.description : ""}
                onChange={(event) =>
                  setInput("description", event.target.value, true)
                }
              />
            </Form.Group>
          </div>
          <div className="col-12">
            <Form.Group className="mb-3" controlId="formGroupCompDetails">
              <Form.Label>
                Company Details <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                placeholder="Company Details"
                defaultValue={details ? details.companyDetails : ""}
                onChange={(event) =>
                  setInput("companyDetails", event.target.value, true)
                }
              />
            </Form.Group>
          </div>
          <div className="col-12 col-sm-6">
            <Button
              variant="secondary"
              type="button"
              className="w-100"
              onClick={() => navigate("/post-job")}
            >
              Cancel
            </Button>
          </div>
          <div className="col-12 col-sm-6">
            <Button
              variant="primary"
              type="button"
              className="w-100"
              disabled={disableSave}
              onClick={handleSubmitBtn}
            >
              Save
            </Button>
          </div>
        </div>
      </Form>
    </>
  );
}

export default EditJobPost;
